/**
 * Patient Order Dispense — field-level effects.
 *
 * Migrated from: patient/patient-order-dispense-base.coffee
 *
 * Old validators → new effects:
 *   AWPLookup                    → AWPLookup
 *   SetFillType                  → SetFillType
 *   IsCaseBlackListDrug          → IsCaseBlackListDrug
 *   MedChange                    → MedChange
 *   CheckForAdditionalApproval   → CheckForAdditionalApproval
 *   CheckIfBillable              → CheckIfBillable
 *   PAPReplacementAppl           → PAPReplacementAppl
 *   CheckForAutoBillable         → CheckForAutoBillable
 *   AdditionalApprovalCheckbox   → AdditionalApprovalCheckbox
 *   CheckClaimAmount             → CheckClaimAmount
 *   NextDispenseCalc             → NextDispenseCalc
 *   VerifyPlanStatus             → VerifyPlanStatus
 *   PatientOrderDispenseBlock    → PatientOrderDispenseBlock
 *   VerifyConfirm                → VerifyConfirm
 *   VerifyDispenseDate           → VerifyDispenseDate
 *   LockInvoicedDispense         → LockInvoicedDispense
 *   VerifyLock                   → VerifyLock
 *   PopFundingPaid               → PopFundingPaid
 *   CalFundingPaid               → CalFundingPaid
 *   VerifyTotalClaim             → VerifyTotalClaim
 *   FixConfirmedCheck            → FixConfirmedCheck
 *   WarningOnChangeClaimOverride → WarningOnChangeClaimOverride
 *   PrefillCopayCard             → PrefillCopayCard
 *   SetCopayUnusedAmt            → SetCopayUnusedAmt
 *   SetCopayCardAmount           → SetCopayCardAmount
 *   Check340BAvailable           → Check340BAvailable
 *   Pricing340BAwpLookup         → Pricing340BAwpLookup
 *
 * New (not from legacy CoffeeScript):
 *   Prefill340BProvider          → prefills is_provider_available from the
 *   linked patient_case's discount_drug_program record when Fill Type is 340B
 *   Prefill340BPricing           → auto-matches and selects the pricing_340b
 *   record (by NDC + provider + effective date) when Fill Type is 340B
 *
 * Skipped:
 *   handle(dfd) init logic — form-load side-effects
 *   populate_add_approval_drgs — init-time data fetch, handled differently
 *   lookup_invoice — internal helper called by AWPLookup
 *   get_awp_from_inventory_id — internal helper called by AWPLookup
 *   get_plan_id / set_claim_check_fields — internal helpers for CheckClaimAmount
 *   get_sub_type / get_active_discount_and_fee_fields — internal calculation helpers
 *   validate_type / lookup_other_fundings — incomplete/unused in old system
 *   check_field_access / tab_do_archive — UI permission helpers, not validators
 *   change_poo_label / change_lable — DOM label manipulation
 *   update_auto_confirm_status / pateint_max_acc — save-time logic (Tier 3)
 *   clear_if_null — init-time cleanup
 */
import dayjs from "dayjs";

import {
	buildApiUrl,
	fetchAPI,
	getSessionRoleSlug,
	getSessionUserID,
	sessionIsAdminLike,
} from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

let isEffectGuardActive = false;

function runWithEffectGuard(callback: () => Promise<void> | void) {
	if (isEffectGuardActive) return;
	isEffectGuardActive = true;
	void Promise.resolve(callback()).finally(() => {
		isEffectGuardActive = false;
	});
}

/** Fields that remain editable when Status=Invoiced and Invoice Close Dt is set. */
const INVOICED_LOCK_EDITABLE_FIELDS = new Set([
	"next_dispense_override",
	"urac_dispense_override_reason",
]);

const INVOICED_LOCK_EFFECT_ID = "lock-invoiced-dispense";

function hasValue(value: unknown): boolean {
	if (value === undefined || value === null || value === "") return false;
	if (Array.isArray(value)) return value.length > 0;
	return true;
}

function readFormValue(
	form: {
		getFieldState: (path: string) => { value?: unknown } | undefined;
		values?: Record<string, unknown>;
	},
	fieldName: string
): unknown {
	const fromState = form.getFieldState(fieldName)?.value;
	if (hasValue(fromState)) return fromState;
	return form.values?.[fieldName];
}

function isInvoicedLocked(form: {
	getFieldState: (path: string) => { value?: unknown } | undefined;
	values?: Record<string, unknown>;
}): boolean {
	const status = readFormValue(form, "status");
	const fiscalDate = readFormValue(form, "fiscal_date");
	return status === "Invoiced" && hasValue(fiscalDate);
}

function applyInvoicedFieldLock(form: {
	query: (pattern: string) => { forEach: (fn: (f: unknown) => void) => void };
	setFieldState: (
		path: string,
		updater: (state: { disabled?: boolean; pattern?: string }) => void
	) => void;
}): void {
	form.query("*").forEach((f) => {
		const path =
			f &&
			typeof f === "object" &&
			"path" in f &&
			(f as { path?: { toString?: () => string } }).path
				? String((f as { path: { toString: () => string } }).path)
				: "";
		const fieldName = path.split(".").pop() ?? path;
		if (!fieldName) return;

		const keepEditable = INVOICED_LOCK_EDITABLE_FIELDS.has(fieldName);
		form.setFieldState(fieldName, (s) => {
			s.disabled = !keepEditable;
			s.pattern = keepEditable ? "editable" : "disabled";
		});
	});
}

// ─── Helper: fetch additional approval brand IDs ────────────────────────────
// The old system loaded this at init time synchronously.
// We cache it lazily and refresh as needed.
let _additionalApproverBrandsCache: number[] | null = null;
async function getAdditionalApproverBrands(): Promise<number[]> {
	if (_additionalApproverBrandsCache) return _additionalApproverBrandsCache;
	try {
		const data = await fetchAPI(
			buildApiUrl(
				"/drug_brand_short/list?limit=1000&_additional_approval=eq:Yes"
			)
		);
		const rows = Array.isArray(data) ? data : (data?.data ?? []);
		const ids = rows.map((r: any) => r.id);
		_additionalApproverBrandsCache = ids;
		return ids;
	} catch {
		return [];
	}
}

const ADDITIONAL_APPROVER_IDS = [14, 137, 398, 123];

// ─── Helper: get pricing data for a plan at a given dispense date ───────────
function getActivePricingRow(plan: any, dispenseDate: string): any | null {
	if (!plan?.pricing_history || !Array.isArray(plan.pricing_history)) {
		return null;
	}
	const dDate = dayjs(dispenseDate, "YYYY-MM-DD");
	const match = plan.pricing_history.find((row: any) => {
		const start = dayjs(row.start_date);
		const end = row.end_date ? dayjs(row.end_date) : dayjs("2099-12-31");
		return (
			dDate.isSame(start, "day") ||
			dDate.isSame(end, "day") ||
			(dDate.isAfter(start) && dDate.isBefore(end))
		);
	});
	return match || null;
}

function resolveAwpPriceFromHistory(
	history: Array<{ date?: string; awp?: unknown }> | undefined,
	fallbackAwp: unknown,
	referenceDate?: string
): number | null {
	const fallback =
		fallbackAwp !== null && fallbackAwp !== undefined
			? parseFloat(String(fallbackAwp))
			: null;
	if (!history?.length) {
		return fallback !== null && !Number.isNaN(fallback) ? fallback : null;
	}

	const refMs = referenceDate
		? dayjs(referenceDate, "YYYY-MM-DD").endOf("day").valueOf()
		: null;

	let bestMs = -1;
	let bestAwp: number | null = null;

	for (const entry of history) {
		const parsed = Date.parse(String(entry.date ?? ""));
		if (Number.isNaN(parsed)) continue;
		const entryAwp = parseFloat(String(entry.awp));
		if (Number.isNaN(entryAwp)) continue;

		if (refMs !== null) {
			if (parsed <= refMs && parsed > bestMs) {
				bestMs = parsed;
				bestAwp = entryAwp;
			}
		} else if (parsed > bestMs) {
			bestMs = parsed;
			bestAwp = entryAwp;
		}
	}

	if (bestAwp !== null) return bestAwp;
	return fallback !== null && !Number.isNaN(fallback) ? fallback : null;
}

function resolveRelationID(value: unknown): unknown {
	if (
		value !== null &&
		value !== undefined &&
		typeof value === "object" &&
		!Array.isArray(value)
	) {
		return (value as Record<string, unknown>).id;
	}
	return value;
}

function setAwpOnForm(form: any, awpPrice: number | null) {
	if (awpPrice === null || Number.isNaN(awpPrice)) return;

	const currentAwp = parseFloat(form.getFieldState("awp")?.value);
	if (currentAwp === awpPrice) return;

	// AWP has validate: CheckClaimAmount — do not re-touch dispensed_quantity
	// just to force a recalc (that caused edit-mode reload/zero cascades).
	form.setFieldState("awp", (s: any) => {
		s.value = awpPrice;
	});
}

function setAwp340bOnForm(form: any, awpPrice: number | null) {
	if (awpPrice === null || Number.isNaN(awpPrice)) return;

	const current = parseFloat(form.getFieldState("awp_340b")?.value);
	if (current === awpPrice) return;

	// awp_340b has validate: Calculate340BFee — setting it here triggers the
	// 340B fee recalculation.
	form.setFieldState("awp_340b", (s: any) => {
		s.value = awpPrice;
	});
}

/** User-editable inputs that should recompute Dispense QA amounts. */
const CLAIM_AMOUNT_USER_TRIGGERS = new Set([
	"dispensed_quantity",
	"dosage",
	"patient_oop",
	"plan_pay",
	"claim_amount_override",
]);

function isFieldUserModified(field: {
	modified?: boolean;
	selfModified?: boolean;
}): boolean {
	return Boolean(field.modified || field.selfModified);
}

function getTriggerFieldName(field: {
	path?: { toString?: () => string } | string;
}): string {
	const path =
		typeof field.path === "string"
			? field.path
			: (field.path?.toString?.() ?? "");
	return path.split(".").pop() ?? path;
}

async function lookupAwpFromPricing340B(
	form: any,
	pricing340bID: unknown,
	dispenseDate?: string
) {
	try {
		const pricingData = await fetchAPI(
			buildApiUrl(`/pricing_340b/${pricing340bID}`)
		);
		const row = Array.isArray(pricingData) ? pricingData[0] : pricingData;
		if (!row) return;

		const awpPrice = resolveAwpPriceFromHistory(
			row.awp_history,
			row.awp,
			dispenseDate
		);
		setAwp340bOnForm(form, awpPrice);
	} catch (err) {
		console.error("lookupAwpFromPricing340B: API error", err);
	}
}

async function syncDispenseAwp(
	form: any,
	opts: { medId?: unknown; dispenseDate?: string }
) {
	// awp (inventory reference price) and awp_340b (340B supplier's
	// contracted price) are independent values from independent tables —
	// selecting a 340B supplier must not stop awp/package_quantity from
	// staying in sync with the drug + dispense date.
	const type = form.getFieldState("type")?.value;
	const biosimilarDrugId =
		form.getFieldState("biosimilar_drug_id")?.value?.id ??
		form.getFieldState("biosimilar_drug_id")?.value;
	const awpMedId =
		type === "Biosimilar" && biosimilarDrugId
			? biosimilarDrugId
			: opts.medId;

	if (opts.dispenseDate && awpMedId) {
		// Biosimilar AWP comes from biosimilar_drug_id; do not overwrite
		// short_brand_id (Drug Name filter) with the biosimilar brand.
		await lookupAwpFromInventory(form, awpMedId, opts.dispenseDate, {
			updateShortBrand: type !== "Biosimilar",
		});
	}

	const pricingID = form.getFieldState("pricing_340b")?.value?.id;
	if (type === "340B" && pricingID) {
		await lookupAwpFromPricing340B(form, pricingID, opts.dispenseDate);
	}
}

// ─── AWPLookup ──────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.AWPLookup
// Trigger field: case_id / dispense_date
// Complex chain: fetches case data, then inventory AWP, sets medication fields,
// also checks for late dispense and looks up invoice data.
export const AWPLookup: FieldHandler = (field, { form, editID }) => {
	runWithEffectGuard(async () => {
		const caseId = form.getFieldState("case_id")?.value?.id;
		const dispenseDate = form.getFieldState("dispense_date")?.value;
		if (!caseId) return;

		// Check late dispense
		if (dispenseDate) {
			const daysDiff = dayjs().diff(
				dayjs(dispenseDate, "YYYY-MM-DD"),
				"day"
			);
			form.setFieldState("trigger_late_dispense_reason", (s) => {
				s.value = daysDiff > 7 ? "Yes" : "No";
			});
		}

		// Lookup invoice if editing existing record
		if (editID) {
			try {
				const claimData = await fetchAPI(
					buildApiUrl(
						`/plan_claim_dispense/list?_original_dispense_id=eq:${editID}`
					)
				);
				const claimRows = Array.isArray(claimData)
					? claimData
					: (claimData?.data ?? []);
				if (claimRows.length > 0 && claimRows[0]) {
					const [claim] = claimRows;
					const localtime = new Date(`${claim.created_on} UTC`);
					form.setFieldState("confirmed_by", (s) => {
						s.value = claim.created_by
							? claim.created_by.username
							: "";
					});
					form.setFieldState("confirmed_on", (s) => {
						s.value = localtime.toString();
					});
					form.setFieldState("invoice_id", (s) => {
						s.value = claim.invoice_id;
					});
					form.setFieldState("invoice_date", (s) => {
						s.value = claim.invoice_date;
					});
				}
			} catch {
				// invoice lookup is best-effort
			}
		}

		form.setFieldState("show_ndc_verify", (s) => {
			s.value = "No";
		});

		try {
			const caseData = await fetchAPI(
				buildApiUrl(`/patient_case/${caseId}`)
			);
			const caseRow = Array.isArray(caseData) ? caseData[0] : caseData;
			if (!caseRow) return;

			form.setFieldState("case_status", (s) => {
				s.value = caseRow.status;
			});
			form.setFieldState("cap_override", (s) => {
				s.value = caseRow.cap_override;
			});
			form.setFieldState("medical_retail", (s) => {
				s.value = caseRow.medical_retail;
			});

			const prevShortName = form.getFieldState(
				"medication_short_name"
			)?.value;
			form.setFieldState("medication_short_name", (s) => {
				s.value = caseRow.medication_short_name;
			});
			form.setFieldState("short_brand_id", (s) => {
				s.value = resolveRelationID(
					caseRow.medication_id?.brand_name_id
				);
			});

			if (
				field.path.toString() === "case_id" &&
				caseRow.medication_short_name !== prevShortName
			) {
				form.setFieldState("medication_id", (s) => {
					s.value = "";
				});
			}

			const medId =
				form.getFieldState("medication_id")?.value?.id ||
				caseRow.medication_id?.id;
			if (dispenseDate) {
				await syncDispenseAwp(form, { medId, dispenseDate });
			}

			if (
				!form.getFieldState("medication_id")?.value?.id &&
				caseRow.medication_id
			) {
				form.setFieldState("medication_id", (s) => {
					s.value = { ...caseRow.medication_id };
				});
			}

			if (caseRow.verified_ndc !== "Yes") {
				form.setFieldState("show_ndc_verify", (s) => {
					s.value = "Yes";
				});
			}
		} catch (err) {
			console.error("AWPLookup: API error", err);
		}
	});
	return "";
};

async function lookupAwpFromInventory(
	form: any,
	inventoryId: any,
	dispenseDate?: string,
	opts?: { updateShortBrand?: boolean }
) {
	const updateShortBrand = opts?.updateShortBrand !== false;
	try {
		// Do not blank short_brand_id before fetch — medication_id is filtered
		// by {short_brand_id}; clearing it makes RelationSelect wipe Drug Name.
		const invData = await fetchAPI(
			buildApiUrl(`/inventory/${inventoryId}`)
		);
		const inv = Array.isArray(invData) ? invData[0] : invData;
		if (!inv) return;

		if (updateShortBrand) {
			form.setFieldState("short_brand_id", (s: any) => {
				s.value = resolveRelationID(inv.brand_name_id);
			});
		}

		form.setFieldState("medication_id", (s: any) => {
			const currentValue = s.value;
			if (
				currentValue?.id === inventoryId &&
				currentValue?.ndc !== inv.ndc
			) {
				s.value = { ...currentValue, ndc: inv.ndc };
			}
		});

		const awpPrice = resolveAwpPriceFromHistory(
			inv.awp_history,
			inv.awp_price,
			dispenseDate
		);
		setAwpOnForm(form, awpPrice);

		form.setFieldState("package_quantity", (s: any) => {
			s.value = inv.package_quantity;
		});
		form.setFieldState("billing_type", (s: any) => {
			s.value = inv.billing_type;
		});
	} catch (err) {
		console.error("lookupAwpFromInventory: API error", err);
	}
}

// ─── SetFillType ────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetFillType
// Trigger field: case_id
// Auto-sets the fill type based on the case's approved PAP/Bridge/CAP/340B subforms.
// Only runs in create mode (add/addfill).
export const SetFillType: FieldHandler = (field, { form, formMode }) => {
	if (formMode === "edit") return "";

	void (async () => {
		const caseId = form.getFieldState("case_id")?.value?.id;
		if (!caseId) return;

		try {
			const caseData = await fetchAPI(
				buildApiUrl(`/patient_case/${caseId}`)
			);
			const caseRow = Array.isArray(caseData) ? caseData[0] : caseData;
			if (!caseRow) return;

			const hasPap =
				Array.isArray(caseRow.subform_pap) &&
				caseRow.subform_pap.some(
					(p: { status?: string }) =>
						p.status === "Approved" || p.status === "Renewal Pending"
				);
			const hasBiosimilar =
				Array.isArray(caseRow.subform_biosimilar) &&
				caseRow.subform_biosimilar.some(
					(b: { status?: string }) => b.status === "Approved"
				);
			const hasBridge =
				caseRow.subform_bridge?.length > 0 &&
				caseRow.subform_bridge[0]?.status === "Approved";
			const hasCap =
				caseRow.subform_patient_cap?.length > 0 &&
				caseRow.subform_patient_cap[0]?.status === "Approved";
			const has340b =
				caseRow.subform_340b?.length > 0 &&
				caseRow.subform_340b[0]?.status === "Approved";

			const loadPrevType = async (): Promise<string | undefined> => {
				const prevDisp = await fetchAPI(
					buildApiUrl(
						`/patient_order_dispense/list?_case_id=eq:${caseId}&sort=-id&limit=1`
					)
				);
				const prevRows = Array.isArray(prevDisp)
					? prevDisp
					: (prevDisp?.data ?? []);
				return prevRows[0]?.type;
			};

			// Approved Biosimilar wins over PAP / Bridge / Copay / 340B.
			if (hasBiosimilar) {
				form.setFieldState("type", (s) => {
					s.value = "Biosimilar";
				});
				return;
			}

			// Prefer Bridge over PAP when both approved (post–PAP-denied path).
			const primaryFunding = hasBridge
				? "Bridge Program"
				: hasPap
					? "PAP"
					: null;

			if (primaryFunding && hasCap) {
				const prevType = await loadPrevType();
				form.setFieldState("type", (s) => {
					s.value =
						prevType === primaryFunding ? "Copay" : primaryFunding;
				});
			} else if (primaryFunding && has340b) {
				const prevType = await loadPrevType();
				form.setFieldState("type", (s) => {
					s.value =
						prevType === primaryFunding ? "340B" : primaryFunding;
				});
			} else if (hasCap && has340b) {
				const prevType = await loadPrevType();
				form.setFieldState("type", (s) => {
					s.value = prevType === "Copay" ? "340B" : "Copay";
				});
			} else if (primaryFunding) {
				form.setFieldState("type", (s) => {
					s.value = primaryFunding;
				});
			} else if (hasCap) {
				form.setFieldState("type", (s) => {
					s.value = "Copay";
				});
			} else if (has340b) {
				form.setFieldState("type", (s) => {
					s.value = "340B";
				});
			}
		} catch (err) {
			console.error("SetFillType: API error", err);
		}
	})();
	return "";
};

// ─── IsCaseBlackListDrug ────────────────────────────────────────────────────
// Old: DSLFx.Validators.IsCaseBlackListDrug
// Trigger field: case_id or medication_id
// Checks if the case's drug is blacklisted/unbillable.
export const IsCaseBlackListDrug: FieldHandler = (field, { form }) => "";

// ─── MedChange ──────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.MedChange
// Trigger field: medication_id
// On medication change, looks up inventory and updates AWP.
export const MedChange: FieldHandler = (field, { form }) => {
	runWithEffectGuard(async () => {
		const medicationId = field.value?.id;
		const dispenseDate = form.getFieldState("dispense_date")?.value;

		form.setFieldState("pricing_340b", (s) => {
			s.value = undefined;
		});

		if (!medicationId) return;

		if (dispenseDate) {
			await syncDispenseAwp(form, {
				medId: medicationId,
				dispenseDate,
			});
		}
	});
	return "";
};

// ─── Pricing340BAwpLookup ───────────────────────────────────────────────────
// Trigger field: pricing_340b / dispense_date (via AWPLookup on 340B)
// Always refreshes awp/package_quantity from inventory (drug + dispense
// date), and additionally sets awp_340b from the selected 340B pricing row
// when a supplier is chosen. The two prices are independent.
export const Pricing340BAwpLookup: FieldHandler = (field, { form }) => {
	if (form.getFieldState("type")?.value !== "340B") return "";

	runWithEffectGuard(async () => {
		const pricingID =
			field.value?.id ?? form.getFieldState("pricing_340b")?.value?.id;
		const dispenseDate = form.getFieldState("dispense_date")?.value;

		// awp (inventory reference price) and awp_340b (340B supplier's
		// contracted price) are independent — always keep awp/package_quantity
		// in sync with the drug + dispense date, regardless of supplier.
		const medicationId = form.getFieldState("medication_id")?.value?.id;
		if (dispenseDate && medicationId) {
			await lookupAwpFromInventory(form, medicationId, dispenseDate);
		}

		if (pricingID) {
			await lookupAwpFromPricing340B(
				form,
				pricingID,
				dispenseDate || undefined
			);
		}
	});
	return "";
};

// ─── Calculate340BFee ─────────────────────────────────────────────
// New: not from legacy CoffeeScript
// Trigger field: awp_340b
// Whenever awp_340b is updated (via 340B supplier selection), recalculates
// fee_340b using dispensed_quantity:
//   if 40% of awp_340b < $500: fee_340b = (awp_340b + 500) * dispensed_quantity
//   else:                      fee_340b = (awp_340b + 0.40 * awp_340b) * dispensed_quantity
export const Calculate340BFee: FieldHandler = (field, { form }) => {
	const awp340b = parseFloat(form.getFieldState("awp_340b")?.value);
	if (Number.isNaN(awp340b)) return "";

	const dq = parseFloat(form.getFieldState("dispensed_quantity")?.value) || 0;
	const fortyPercent = 0.4 * awp340b;
	const fee =
		fortyPercent < 500
			? (awp340b + 500) * dq
			: (awp340b + fortyPercent) * dq;

	form.setFieldState("fee_340b", (s) => {
		s.value = fee;
	});
	return "";
};

/**
 * Normalizes an NDC for comparison: strips dashes/whitespace and zero-pads
 * to 11 digits. Mirrors backend/src/common/utils/ndc.ts (normalizeNDC) plus
 * the zero-pad step used when matching against `inventory.ndc`
 * (see backend getInventoryID) — `inventory.ndc` is free text and may be
 * entered with dashes and/or without leading zeros, while `pricing_340b.ndc`
 * is stored normalized (digits only, not padded) by a save trigger.
 */
function normalizeNdcForMatch(raw: unknown): string | null {
	if (!raw) return null;
	const digits = String(raw).replace(/-/g, "").replace(/\s/g, "").trim();
	if (!/^\d{8,11}$/.test(digits)) return null;
	return digits.length < 11 ? "0".repeat(11 - digits.length) + digits : digits;
}

// ─── Prefill340BPricing ─────────────────────────────────────────────────────
// New: not from legacy CoffeeScript
// Trigger field: medication_id / is_provider_available
// When Fill Type is 340B, auto-matches and selects the pricing_340b record for
// the selected drug's NDC + current 340B provider, among rows already
// effective today. Robust to NDC formatting differences between
// inventory.ndc (free text, may have dashes / no leading zeros) and
// pricing_340b.ndc (normalized, digits only, not zero-padded).
export const Prefill340BPricing: FieldHandler = (field, { form }) => {
	if (form.getFieldState("type")?.value !== "340B") return "";

	// Don't clobber a value the user (or a previous load) already selected.
	if (form.getFieldState("pricing_340b")?.value?.id) return "";

	// Not using runWithEffectGuard: it's a single shared mutex across every
	// handler in this file, and MedChange (which also runs on medication_id,
	// earlier in the same field's validate array) already holds it for the
	// duration of its async work — so this effect would never run if guarded.
	void (async () => {
		const medicationId = form.getFieldState("medication_id")?.value?.id;
		const supplierName = form.getFieldState("is_provider_available")?.value;
		if (!medicationId || !supplierName) return;

		try {
			const invData = await fetchAPI(
				buildApiUrl(`/inventory/${medicationId}`)
			);
			const inv = Array.isArray(invData) ? invData[0] : invData;
			const ndc = normalizeNdcForMatch(inv?.ndc);
			if (!ndc) return;

			const today = dayjs().format("YYYY-MM-DD");
			const pricingData = await fetchAPI(
				buildApiUrl(
					`/pricing_340b/list?limit=1000&_supplier_name=eq:${encodeURIComponent(supplierName)}&_effective_date=lte:${today}`
				)
			);
			const rows = Array.isArray(pricingData)
				? pricingData
				: (pricingData?.data ?? []);

			const match = rows.find(
				(row: any) => normalizeNdcForMatch(row.ndc) === ndc
			);
			if (!match) return;

			// Re-check right before writing — form state may have changed while awaiting.
			if (form.getFieldState("pricing_340b")?.value?.id) return;

			form.setFieldState("pricing_340b", (s) => {
				s.value = { id: match.id };
			});
		} catch (err) {
			console.error("Prefill340BPricing: API error", err);
		}
	})();
	return "";
};

// ─── Prefill340BProvider ─────────────────────────────────────────
// New: not from legacy CoffeeScript
// Trigger field: type
// When Fill Type is set to "340B", looks up the linked patient_case's
// discount_drug_program record (subform_340b) and prefills is_provider_available.
export const Prefill340BProvider: FieldHandler = (field, { form }) => {
	if (form.getFieldState("type")?.value !== "340B") return "";

	void (async () => {
		const caseId = form.getFieldState("case_id")?.value?.id;
		if (!caseId) return;

		try {
			const caseData = await fetchAPI(
				buildApiUrl(`/patient_case/${caseId}`)
			);
			const caseRow = Array.isArray(caseData) ? caseData[0] : caseData;
			const programs = caseRow?.subform_340b;
			if (!Array.isArray(programs) || programs.length === 0) return;

			const approved = programs
				.filter((p: any) => p.status === "Approved")
				.sort(
					(a: any, b: any) =>
						new Date(b.updated_on || b.created_on).getTime() -
						new Date(a.updated_on || a.created_on).getTime()
				);
			const program = approved[0] ?? programs[programs.length - 1];
			if (!program?.is_provider_available) return;

			form.setFieldState("is_provider_available", (s) => {
				s.value = program.is_provider_available;
			});
		} catch (err) {
			console.error("Prefill340BProvider: API error", err);
		}
	})();
	return "";
};

// ─── CheckForAdditionalApproval ─────────────────────────────────────────────
// Old: DSLFx.Validators.CheckForAdditionalApproval
// Trigger field: medication_id
// Checks if the medication's brand requires additional approval. Toggles
// visibility of additional_approval and confirmation_alert fields, and
// controls the confirmed checkbox disabled state.
export const CheckForAdditionalApproval: FieldHandler = (
	field,
	{ form, formMode }
) => {
	void (async () => {
		const medicationId = form.getFieldState("medication_id")?.value?.id;
		if (!medicationId) return;

		const billable = form.getFieldState("billable")?.value;
		if (!billable || billable === "No") return;

		const ft = form.getFieldState("type")?.value;
		const papType = form.getFieldState("pap_type")?.value;
		const isReadMode = formMode === "read";
		const invoicedLocked = isInvoicedLocked(form);

		const additionalBrands = await getAdditionalApproverBrands();
		const additionalApproval = form.getFieldState(
			"additional_approval"
		)?.value;
		const isApproved = additionalApproval === "Confirmed";
		const currentUserId = getSessionUserID();
		const canApprove =
			currentUserId !== undefined &&
			ADDITIONAL_APPROVER_IDS.includes(currentUserId);

		try {
			const invData = await fetchAPI(
				buildApiUrl(`/inventory/${medicationId}`)
			);
			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv) return;

			const needsAdditional =
				additionalBrands.includes(inv.brand_name_id?.id) &&
				!(
					ft === "Tracking Only" ||
					(ft === "PAP" && papType === "Replacement Pending")
				);

			if (needsAdditional) {
				form.setFieldState("confirmation_alert", (s) => {
					s.visible = true;
				});
				form.setFieldState("additional_approval", (s) => {
					s.visible = true;
					if (!isReadMode) {
						s.disabled = invoicedLocked || !canApprove;
					}
				});
				if (!isReadMode) {
					form.setFieldState("confirmed", (s) => {
						s.disabled = invoicedLocked || !isApproved;
					});
					if (!isApproved) {
						form.setFieldState("confirmed", (s) => {
							s.value = "";
						});
					}
				}
			} else {
				form.setFieldState("confirmation_alert", (s) => {
					s.visible = false;
				});
				form.setFieldState("additional_approval", (s) => {
					s.visible = false;
				});
				if (!isReadMode) {
					form.setFieldState("confirmed", (s) => {
						s.disabled = invoicedLocked ? true : false;
					});
				}
			}
		} catch (err) {
			console.error("CheckForAdditionalApproval: API error", err);
		}
	})();
	return "";
};

// ─── CheckIfBillable ────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckIfBillable
// Trigger field: type / pap_type / billable
// Controls visibility and enabled state of billable, confirmed, additional_approval fields
// based on fill type and PAP sub-type.
//COMPLICATED-CHECK-FUNCTIONALITY
// The old system heavily manipulated DOM classes and jQuery selectors for buttons.
// This is translated to Formily field state (visible, disabled) where possible.
export const CheckIfBillable: FieldHandler = (field, { form, formMode }) => {
	const ft = form.getFieldState("type")?.value;
	const papType = form.getFieldState("pap_type")?.value;
	const currentUserRole = getSessionRoleSlug();
	const isReadMode = formMode === "read";
	const invoicedLocked = isInvoicedLocked(form);

	if (!ft) {
		form.setFieldState("confirmation_alert", (s) => {
			s.visible = false;
		});
		form.setFieldState("additional_approval", (s) => {
			s.visible = false;
		});
		return "";
	}

	const isTrackingOrPapReplacement =
		ft === "Tracking Only" ||
		(ft === "PAP" && papType === "Replacement Pending");

	if (isTrackingOrPapReplacement) {
		form.setFieldState("billable", (s) => {
			s.visible = true;
			if (!isReadMode) {
				s.value = "No";
				s.disabled = true;
			}
		});
		form.setFieldState("confirmed", (s) => {
			s.visible = false;
		});
		form.setFieldState("confirmation_alert", (s) => {
			s.visible = false;
		});
		form.setFieldState("additional_approval", (s) => {
			s.visible = false;
		});
	} else {
		form.setFieldState("billable", (s) => {
			s.visible = true;
			if (!isReadMode) {
				s.disabled = invoicedLocked || currentUserRole === "cma";
			}
		});
		form.setFieldState("confirmed", (s) => {
			s.visible = true;
			if (!isReadMode) {
				s.disabled = invoicedLocked || currentUserRole === "cma";
			}
		});
	}

	return "";
};

// ─── PAPReplacementAppl ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.PAPReplacementAppl
// Trigger field: pap_type / case_id
// Validates that PAP "Replacement Pending" can only be selected for Medical cases.
export const PAPReplacementAppl: FieldHandler = (field, { form }) => {
	void (async () => {
		const papType = form.getFieldState("pap_type")?.value;
		const fillType = form.getFieldState("type")?.value;
		const caseId = form.getFieldState("case_id")?.value?.id;

		if (!caseId || !papType || !fillType) return;
		if (papType !== "PAP" && papType !== "Replacement Pending") return;

		try {
			const caseData = await fetchAPI(
				buildApiUrl(`/patient_case/${caseId}`)
			);
			const caseRow = Array.isArray(caseData) ? caseData[0] : caseData;
			if (caseRow?.medical_retail !== "Medical") {
				form.setFieldState("pap_type", (s) => {
					s.value = "";
				});
				field.setFeedback({
					type: "warning",
					messages: [
						'PAP "Replacement Pending" could only be selected for Medical case',
					],
				});
			}
		} catch (err) {
			console.error("PAPReplacementAppl: API error", err);
		}
	})();
	return "";
};

// ─── CheckForAutoBillable ───────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckForAutoBillable
// Trigger field: medication_id
// Checks inventory dispense form type for tablet/capsule classification.
export const CheckForAutoBillable: FieldHandler = (
	field,
	{ form, formMode }
) => {
	void (async () => {
		const medicationId = form.getFieldState("medication_id")?.value?.id;
		const billable = form.getFieldState("billable")?.value;

		if (!medicationId) return;
		if (formMode === "edit" && billable === "Yes") return;

		try {
			const invData = await fetchAPI(
				buildApiUrl(`/inventory/${medicationId}`)
			);
			const inv = Array.isArray(invData) ? invData[0] : invData;

			// Old system checked dispense_form for tablet/capsule but didn't
			// take action beyond the check. Preserved for future use.
		} catch (err) {
			console.error("CheckForAutoBillable: API error", err);
		}
	})();
	return "";
};

// ─── AdditionalApprovalCheckbox ─────────────────────────────────────────────
// Old: DSLFx.Validators.AdditionalApprovalCheckbox
// Trigger field: additional_approval
// Enables/disables confirmed checkbox based on additional approval status.
export const AdditionalApprovalCheckbox: FieldHandler = (
	field,
	{ form, formMode }
) => {
	void (async () => {
		if (formMode === "read") return;
		if (isInvoicedLocked(form)) {
			form.setFieldState("confirmed", (s) => {
				s.disabled = true;
			});
			return;
		}

		const medicationId = form.getFieldState("medication_id")?.value?.id;
		if (!medicationId) return;

		const additionalBrands = await getAdditionalApproverBrands();

		try {
			const invData = await fetchAPI(
				buildApiUrl(`/inventory/${medicationId}`)
			);
			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv || !additionalBrands.includes(inv.brand_name_id?.id)) {
				return;
			}

			const isApproved =
				form.getFieldState("additional_approval")?.value ===
				"Confirmed";

			form.setFieldState("confirmed", (s) => {
				s.disabled = !isApproved;
			});
			if (!isApproved) {
				form.setFieldState("confirmed", (s) => {
					s.value = "";
				});
			}
		} catch (err) {
			console.error("AdditionalApprovalCheckbox: API error", err);
		}
	})();
	return "";
};

// ─── CheckClaimAmount ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.CheckClaimAmount
// Trigger field: dispensed_quantity / awp / patient_oop / claim_amount_override
// Complex financial calculation chain:
//   1. Fetch past dispense for claim comparison
//   2. Fetch patient record for plan_id
//   3. Fetch plan for pricing/discount data
//   4. Calculate claim_amount and funding splits by fill type
//      Biosimilar: qty = Dosage (fallback dispensed × 2); others: Dispensed Amount
//COMPLICATED-CHECK-FUNCTIONALITY
export const CheckClaimAmount: FieldHandler = (field, { form, editID }) => {
	void (async () => {
		// Edit mode: keep saved Dispense QA amounts on open. FormDraw runs
		// validators onFieldInit and AWPLookup refreshes AWP — those must not
		// overwrite claim/plan/patient/funding with incomplete (often 0) math.
		// Recalculate only after the user changes a claim input.
		if (editID) {
			const trigger = getTriggerFieldName(field);
			if (!CLAIM_AMOUNT_USER_TRIGGERS.has(trigger)) return;
			if (!isFieldUserModified(field)) return;
		}

		const caseValue =
			form.getFieldState("case_id")?.value ?? form.values?.case_id;
		const caseId =
			typeof caseValue === "object" && caseValue !== null
				? caseValue.id
				: typeof caseValue === "number" || typeof caseValue === "string"
					? caseValue
					: undefined;
		const dispenseDate = form.getFieldState("dispense_date")?.value;
		const dq =
			parseFloat(form.getFieldState("dispensed_quantity")?.value) || 0;
		const awp = parseFloat(form.getFieldState("awp")?.value) || 0;
		const tp = form.getFieldState("type")?.value;
		const dosage =
			parseFloat(form.getFieldState("dosage")?.value) || 0;
		// Biosimilar claim math uses Dosage (dispensed × 2), not Dispensed Amount.
		const fillQty =
			tp === "Biosimilar" ? (dosage > 0 ? dosage : dq * 2) : dq;
		const oop = parseFloat(form.getFieldState("patient_oop")?.value) || 0;
		const cmOvr =
			parseFloat(form.getFieldState("claim_amount_override")?.value) || 0;

		if (!caseId) return;

		// Incomplete pricing inputs — do not wipe existing QA amounts to zero.
		const existingClaim =
			parseFloat(String(form.getFieldState("claim_amount")?.value)) || 0;
		if ((awp <= 0 || fillQty <= 0) && existingClaim > 0) return;

		// Check package quantity warning
		const pkqty =
			parseInt(form.getFieldState("package_quantity")?.value) || 0;
		if (pkqty > 0 && dq % pkqty !== 0) {
			form.setFieldState("show_package_warning", (s) => {
				s.value = "Yes";
			});
		} else {
			form.setFieldState("show_package_warning", (s) => {
				s.value = "No";
			});
		}

		try {
			// Fetch past dispense for comparison
			let pastClaimDelta: number | null = null;
			let pastDispenseUrl = `/patient_order_dispense/list?sort=-id&limit=1&_case_id=eq:${caseId}`;
			if (editID) {
				pastDispenseUrl += `&_id=ne:${editID}`;
			}
			const pastData = await fetchAPI(buildApiUrl(pastDispenseUrl));
			const pastRows = Array.isArray(pastData)
				? pastData
				: (pastData?.data ?? []);
			if (pastRows.length > 0) {
				pastClaimDelta = pastRows[0].claim_amount;
			}

			// Fetch patient to get plan_id
			const patientValue =
				form.getFieldState("patient_id")?.value ??
				form.values?.patient_id;
			const patientId =
				typeof patientValue === "object" && patientValue !== null
					? patientValue.id
					: typeof patientValue === "number" ||
						  typeof patientValue === "string"
						? patientValue
						: undefined;
			if (!patientId) return;

			const ptData = await fetchAPI(buildApiUrl(`/patient/${patientId}`));
			const patient = Array.isArray(ptData) ? ptData[0] : ptData;
			if (!patient?.plan_id?.id) return;

			// Fetch plan for pricing
			const planData = await fetchAPI(
				buildApiUrl(`/plan/${patient.plan_id?.id}`)
			);
			const plan = Array.isArray(planData) ? planData[0] : planData;
			if (!plan) return;

			// Calculate claim amount
			const pricingData = getActivePricingRow(plan, dispenseDate);
			const discountPercent = pricingData?.discount_percent
				? parseFloat(pricingData.discount_percent)
				: 0;
			const costPerUnit = awp * ((100.0 - discountPercent) / 100.0);
			let claimAmount = costPerUnit * fillQty;

			form.setFieldState("claim_amount", (s) => {
				s.value = claimAmount;
			});
			form.setFieldState("plan_rebate", (s) => {
				s.value = plan.rebate;
			});

			if (cmOvr > 0) {
				claimAmount = cmOvr;
			}

			if (pastClaimDelta && claimAmount > 0) {
				const perChange = Math.abs(
					(claimAmount - pastClaimDelta) / claimAmount
				);
				form.setFieldState("percentage_change", (s) => {
					s.value = perChange;
				});
			}

			// Split amounts by fill type
			if (
				tp === "PAP" ||
				tp === "Bridge Program" ||
				tp === "Biosimilar" ||
				tp === "340B"
			) {
				const fundingAmount = claimAmount - oop;
				form.setFieldState("plan_amount", (s) => {
					s.value = 0;
				});
				form.setFieldState("funding_amount", (s) => {
					s.value = fundingAmount;
				});
				form.setFieldState("patient_amount", (s) => {
					s.value = oop;
				});
				form.setFieldState("funding_paid", (s) => {
					s.value = "";
				});
			} else if (tp === "Tracking Only") {
				const planPay =
					parseFloat(form.getFieldState("plan_pay")?.value) || 0;
				const planAmount = claimAmount - oop - planPay;
				form.setFieldState("plan_amount", (s) => {
					s.value = planAmount;
				});
				form.setFieldState("funding_amount", (s) => {
					s.value = 0;
				});
				form.setFieldState("patient_amount", (s) => {
					s.value = oop;
				});
				form.setFieldState("funding_paid", (s) => {
					s.value = "";
				});

				if (planAmount < 0) {
					form.setFieldState("patient_oop", (s) => {
						s.value = "";
					});
					form.setFieldState("plan_pay", (s) => {
						s.value = "";
					});
					form.setFieldState("funding_paid", (s) => {
						s.value = claimAmount;
					});
				}
			} else if (tp === "Copay") {
				form.setFieldState("patient_amount", (s) => {
					s.value = oop;
				});
			} else {
				const fundingAmount = claimAmount - oop;
				form.setFieldState("plan_amount", (s) => {
					s.value = 0;
				});
				form.setFieldState("patient_amount", (s) => {
					s.value = oop;
				});
				form.setFieldState("funding_paid", (s) => {
					s.value = "";
				});
				form.setFieldState("funding_amount", (s) => {
					s.value = fundingAmount;
				});
			}
		} catch (err) {
			console.error("CheckClaimAmount: API error", err);
		}
	})();
	return "";
};

// ─── NextDispenseCalc ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.NextDispenseCalc
// Trigger field: dispense_date / days_supply
// Calculates next_dispense_date from dispense_date + days_supply.
// Biosimilar claim qty uses Dosage instead of Dispensed Amount (see CheckClaimAmount);
// next dispense still uses Days Supply for all fill types.
export const NextDispenseCalc: FieldHandler = (field, { form }) => {
	const dispenseDate = form.getFieldState("dispense_date")?.value;
	const daysSupply = form.getFieldState("days_supply")?.value;
	const deliveryDate = form.getFieldState("delivery_date")?.value;
	const nextDispenseDate = form.getFieldState("next_dispense_date")?.value;

	if (!dispenseDate || !daysSupply) return "";
	if (deliveryDate && nextDispenseDate) return "";

	const nd = dayjs(dispenseDate, "YYYY-MM-DD")
		.add(parseInt(String(daysSupply), 10), "day")
		.format("YYYY-MM-DD");
	if (nextDispenseDate !== nd) {
		form.setFieldState("next_dispense_date", (s) => {
			s.value = nd;
		});
	}
	return "";
};

// ─── BiosimilarDosageCalc ───────────────────────────────────────────────────
// Trigger field: dispensed_quantity
// Dosage = Dispensed Amount × 2 (Biosimilar fills only).
export const BiosimilarDosageCalc: FieldHandler = (field, { form }) => {
	if (form.getFieldState("type")?.value !== "Biosimilar") return "";
	const dq = parseFloat(form.getFieldState("dispensed_quantity")?.value);
	if (Number.isNaN(dq)) return "";
	const dosage = dq * 2;
	const current = parseFloat(form.getFieldState("dosage")?.value);
	if (current !== dosage) {
		form.setFieldState("dosage", (s) => {
			s.value = dosage;
		});
	}
	return "";
};

// ─── BiosimilarPrefill ──────────────────────────────────────────────────────
// Trigger field: type
// Prefills brand/drug from approved biosimilar (or PAP/Copay deny selections);
// refreshes AWP from biosimilar drug without touching Drug Name's brand filter.
export const BiosimilarPrefill: FieldHandler = (field, { form }) => {
	if (form.getFieldState("type")?.value !== "Biosimilar") return "";

	void (async () => {
		const caseId = form.getFieldState("case_id")?.value?.id;
		if (!caseId) return;

		try {
			const caseData = await fetchAPI(
				buildApiUrl(`/patient_case/${caseId}`)
			);
			const caseRow = Array.isArray(caseData) ? caseData[0] : caseData;
			if (!caseRow) return;

			const biosimilarRows = Array.isArray(caseRow.subform_biosimilar)
				? caseRow.subform_biosimilar
				: [];
			const approvedBiosimilar =
				biosimilarRows.find(
					(b: { status?: string }) => b.status === "Approved"
				) ??
				biosimilarRows.find(
					(b: { status?: string }) => b.status === "Pending"
				);

			const papRows = Array.isArray(caseRow.subform_pap)
				? caseRow.subform_pap
				: [];
			const pap =
				papRows.find(
					(p: {
						status?: string;
						is_biosimilar_available?: string;
					}) =>
						p.status === "Denied" &&
						p.is_biosimilar_available === "Yes"
				) ??
				papRows.find((p: { status?: string }) => p.status === "Denied");

			const copayRows = Array.isArray(caseRow.subform_patient_cap)
				? caseRow.subform_patient_cap
				: [];
			const copay =
				copayRows.find(
					(c: {
						status?: string;
						is_biosimilar_available?: string;
					}) =>
						c.status === "Denied" &&
						c.is_biosimilar_available === "Yes"
				) ??
				copayRows.find(
					(c: { status?: string }) => c.status === "Denied"
				);

			const brand =
				approvedBiosimilar?.biosimilar_brand_offered ??
				pap?.biosimilar_brand_offered ??
				copay?.biosimilar_brand_offered;
			const drug =
				approvedBiosimilar?.biosimilar_drug_id ??
				pap?.biosimilar_drug_id ??
				copay?.biosimilar_drug_id;

			if (brand) {
				form.setFieldState("biosimilar_brand_offered", (s) => {
					s.value = brand;
				});
			}
			if (drug) {
				form.setFieldState("biosimilar_drug_id", (s) => {
					s.value = drug;
				});
				const dispenseDate = form.getFieldState("dispense_date")?.value;
				const drugId =
					typeof drug === "object" && drug !== null
						? (drug as { id?: number }).id
						: drug;
				if (drugId) {
					await lookupAwpFromInventory(form, drugId, dispenseDate, {
						updateShortBrand: false,
					});
				}
			}
		} catch (err) {
			console.error("BiosimilarPrefill: API error", err);
		}
	})();
	return "";
};

// ─── BiosimilarAwpLookup ────────────────────────────────────────────────────
// Trigger field: biosimilar_drug_id
// Sets AWP from the selected Biosimilar inventory item.
export const BiosimilarAwpLookup: FieldHandler = (field, { form }) => {
	if (form.getFieldState("type")?.value !== "Biosimilar") return "";

	runWithEffectGuard(async () => {
		const drugId = field.value?.id ?? field.value;
		if (!drugId) return;
		const dispenseDate = form.getFieldState("dispense_date")?.value;
		await lookupAwpFromInventory(form, drugId, dispenseDate, {
			updateShortBrand: false,
		});
	});
	return "";
};

// ─── VerifyPlanStatus ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.VerifyPlanStatus
// Trigger field: confirmed
// Prevents confirming dispense when the patient's plan status is "Pending".
export const VerifyPlanStatus: FieldHandler = (field, { form }) => {
	void (async () => {
		const confirmed = form.getFieldState("confirmed")?.value;
		if (!confirmed) return;

		const patientId = form.getFieldState("patient_id")?.value?.id;
		if (!patientId) return;

		try {
			const ptData = await fetchAPI(buildApiUrl(`/patient/${patientId}`));
			const patient = Array.isArray(ptData) ? ptData[0] : ptData;
			if (!patient?.plan_id?.id) return;

			const planData = await fetchAPI(
				buildApiUrl(`/plan/${patient.plan_id?.id}`)
			);
			const plan = Array.isArray(planData) ? planData[0] : planData;
			if (plan?.status === "Pending") {
				form.setFieldState("confirmed", (s) => {
					s.value = "";
				});
				field.setFeedback({
					type: "error",
					messages: [
						`For Plan ${plan.auto_name}, you cannot confirm dispense as the Plan Status is "Pending"`,
					],
				});
			}
		} catch (err) {
			console.error("VerifyPlanStatus: API error", err);
		}
	})();
	return "";
};

// ─── PatientOrderDispenseBlock ──────────────────────────────────────────────
// Old: DSLFx.Validators.PatientOrderDispenseBlock
// Trigger field: (form-level block validator with allowed_fields prop)
// Blocks field editing for confirmed/invoiced dispenses.
// Props: { allowed_fields: string[] }
//COMPLICATED-CHECK-FUNCTIONALITY
// The old system used this as a 4-param form-level validator checking Auth.is_admin().
// In the new system, field-level disabling should be handled by backend permissions
// or a form-level effect. Preserved for reference.
export const PatientOrderDispenseBlock: FieldHandler = (field, { form }) => {
	const confirmed = form.getFieldState("confirmed")?.value;
	const invoiceId = form.getFieldState("invoice_id")?.value;
	const isAdmin = sessionIsAdminLike();

	const isConfirmed =
		confirmed === "Confirmed" ||
		(Array.isArray(confirmed) && confirmed.includes("Confirmed"));

	if (isAdmin) {
		if (invoiceId) {
			// Admin with invoice: only allowed fields editable
			// Field-level disabling would need to be handled per-field
		}
	} else if (isConfirmed) {
		// Non-admin confirmed: only allowed fields editable
	}

	return "";
};

// ─── VerifyConfirm ──────────────────────────────────────────────────────────
// Old: DSLFx.Validators.VerifyConfirm
// Trigger field: confirmed
// Validates case status before allowing confirmation. Only approved/pending cases
// can have confirmed dispenses (for CM role).
export const VerifyConfirm: FieldHandler = (field, { form }) => {
	const confirmed = form.getFieldState("confirmed")?.value;
	const currentUserRole = getSessionRoleSlug();

	if (sessionIsAdminLike()) return "";

	const isConfirmed =
		(Array.isArray(confirmed) &&
			confirmed.length > 0 &&
			confirmed[0] === "Confirmed") ||
		confirmed === "Confirmed";
	if (!isConfirmed) return "";

	const caseStatus = form.getFieldState("case_status")?.value;
	if (!caseStatus) return "";

	if (
		caseStatus !== "Approved" &&
		caseStatus !== "Pending" &&
		currentUserRole === "cm"
	) {
		form.setFieldState("confirmed", (s) => {
			s.value = "";
		});
		field.setFeedback({
			type: "error",
			messages: [
				`The case status is "${caseStatus}". Only dispenses from an Approved or Pending case are allowed to be confirmed.`,
			],
		});
	}
	return "";
};

// ─── LockInvoicedDispense ───────────────────────────────────────────────────
// Trigger field: status / fiscal_date / confirmed / dispense_date
// When Status is Invoiced and Invoice Close Dt (fiscal_date) has a value,
// disable all fields in edit mode except next-dispense override fields.
export const LockInvoicedDispense: FieldHandler = (
	_field,
	{ form, formMode }
) => {
	if (formMode === "read") return "";
	if (!isInvoicedLocked(form)) return "";

	const applyLock = () => {
		if (!isInvoicedLocked(form)) return;
		applyInvoicedFieldLock(form);
	};

	applyLock();

	// FormDraw / other effects may re-enable fields after init; re-apply.
	form.removeEffects?.(INVOICED_LOCK_EFFECT_ID);
	form.addEffects?.(INVOICED_LOCK_EFFECT_ID, () => {
		applyLock();
	});
	queueMicrotask(applyLock);
	setTimeout(applyLock, 0);
	setTimeout(applyLock, 100);
	setTimeout(applyLock, 300);
	return "";
};

// ─── VerifyDispenseDate ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.VerifyDispenseDate
// Trigger field: next_dispense_date / next_dispense_override
// Calculates final_next_dispense_date from the override or next_dispense_date.
export const VerifyDispenseDate: FieldHandler = (field, { form }) => {
	const ndd = form.getFieldState("next_dispense_date")?.value;
	const nddo = form.getFieldState("next_dispense_override")?.value;

	if (!ndd) return "";

	let fndd = dayjs(ndd);
	if (nddo) {
		fndd = dayjs(nddo);
	}

	const current = form.getFieldState("final_next_dispense_date")?.value;
	if (dayjs(current).format("YYYY-MM-DD") !== fndd.format("YYYY-MM-DD")) {
		form.setFieldState("final_next_dispense_date", (s) => {
			s.value = fndd.format("YYYY-MM-DD");
		});
	}
	return "";
};

// ─── VerifyLock ─────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.VerifyLock
// Trigger field: (various fields on confirmed dispenses)
// Locks field editing for confirmed dispenses.
//COMPLICATED-CHECK-FUNCTIONALITY
// The old system used DOM manipulation to disable fields. In the new system,
// field-level locking should be handled via backend permissions or form patterns.
export const VerifyLock: FieldHandler = (field, { form }) => {
	const confirmed = form.getFieldState("confirmed")?.value;
	const isConfirmed =
		confirmed === "Confirmed" ||
		(Array.isArray(confirmed) && confirmed.includes("Confirmed"));

	if (isConfirmed) {
		field.setFeedback({
			type: "warning",
			messages: ["This dispense is confirmed and locked for editing."],
		});
	}
	return "";
};

// ─── PopFundingPaid ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.PopFundingPaid
// Trigger field: funding_paid / claim_amount
// Defaults funding_paid to claim_amount if funding_paid is empty and claim > 0.
export const PopFundingPaid: FieldHandler = (field, { form }) => {
	const fundingPaid = form.getFieldState("funding_paid")?.value;
	const claimAmount =
		parseFloat(form.getFieldState("claim_amount")?.value) || 0;

	if (
		!fundingPaid &&
		claimAmount !== 0 &&
		String(claimAmount) !== String(fundingPaid)
	) {
		form.setFieldState("funding_paid", (s) => {
			s.value = claimAmount;
		});
	}
	return "";
};

// ─── CalFundingPaid ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.CalFundingPaid
// Trigger field: dispensed_quantity / patient_oop / plan_pay / funding_paid
// Recalculates funding_paid as claim_amount - plan_pay - patient_oop.
export const CalFundingPaid: FieldHandler = (field, { form }) => {
	const fundingPaid = form.getFieldState("funding_paid")?.value;
	if (!fundingPaid) return "";

	const oop = parseFloat(form.getFieldState("patient_oop")?.value) || 0;
	const planPay = parseFloat(form.getFieldState("plan_pay")?.value) || 0;
	const claimAmount =
		parseFloat(form.getFieldState("claim_amount")?.value) || 0;

	const calculated = claimAmount - planPay - oop;
	if (calculated > 0) {
		form.setFieldState("funding_paid", (s) => {
			s.value = calculated;
		});
	}
	return "";
};

// ─── VerifyTotalClaim ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.VerifyTotalClaim
// Trigger field: plan_pay / patient_oop
// Verifies that plan_pay + patient_oop equals claim_amount.
// Returns a warning if they don't match.
export const VerifyTotalClaim: FieldHandler = (field, { form }) => {
	const planPay = parseFloat(form.getFieldState("plan_pay")?.value) || 0;
	const oop = parseFloat(form.getFieldState("patient_oop")?.value) || 0;
	const claimAmount =
		parseFloat(form.getFieldState("claim_amount")?.value) || 0;

	if (!isNaN(planPay) && !isNaN(oop)) {
		const total = planPay + oop;
		if (total !== claimAmount) {
			field.setFeedback({
				type: "warning",
				messages: [
					`Plan Pay (${planPay}) + Patient OOP (${oop}) = ${total} does not equal Net Claim Amount (${claimAmount})`,
				],
			});
		} else {
			field.setFeedback({ type: "warning", messages: [] });
		}
	}
	return "";
};

// ─── FixConfirmedCheck ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.FixConfirmedCheck
// Trigger field: confirmed / billable
// Disables billable field when dispense is confirmed and billable.
export const FixConfirmedCheck: FieldHandler = (field, { form, formMode }) => {
	if (formMode === "read") return "";
	if (isInvoicedLocked(form)) {
		form.setFieldState("billable", (s) => {
			s.disabled = true;
		});
		return "";
	}

	const confirmed = form.getFieldState("confirmed")?.value;
	const billable = form.getFieldState("billable")?.value;

	const isConfirmed =
		confirmed === "Confirmed" ||
		(Array.isArray(confirmed) &&
			confirmed.length > 0 &&
			confirmed[0] === "Confirmed");

	if (billable === "Yes" && isConfirmed) {
		form.setFieldState("billable", (s) => {
			s.disabled = true;
		});
	} else {
		form.setFieldState("billable", (s) => {
			s.disabled = false;
		});
	}
	return "";
};

// ─── WarningOnChangeClaimOverride ───────────────────────────────────────────
// Old: DSLFx.Validators.WarningOnChangeClaimOverride
// Trigger field: claim_amount_override
// Shows a warning when claim_amount_override is changed.
export const WarningOnChangeClaimOverride: FieldHandler = (field, { form }) => {
	const overrideVal = form.getFieldState("claim_amount_override")?.value;
	if (overrideVal) {
		field.setFeedback({
			type: "warning",
			messages: [
				"The value of Net Claim Amount Override has been changed which will affect the overall calculations.",
			],
		});
	} else {
		field.setFeedback({ type: "warning", messages: [] });
	}
	return "";
};

// ─── PrefillCopayCard ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.PrefillCopayCard
// Trigger field: type / case_id
// Auto-selects the most recently approved copay card from the case for Copay fills.
export const PrefillCopayCard: FieldHandler = (field, { form, formMode }) => {
	void (async () => {
		const fillType = form.getFieldState("type")?.value;
		if (fillType !== "Copay") return;

		if (formMode === "edit") {
			const existingCard = form.getFieldState("copay_card")?.value;
			if (existingCard) return;
		}

		const caseId = form.getFieldState("case_id")?.value?.id;
		if (!caseId) return;

		try {
			const caseData = await fetchAPI(
				buildApiUrl(`/patient_case/${caseId}`)
			);
			const caseRow = Array.isArray(caseData) ? caseData[0] : caseData;
			if (!caseRow) return;

			const copays = (caseRow.subform_patient_cap || [])
				.filter((item: any) => item.status === "Approved")
				.sort(
					(a: any, b: any) =>
						new Date(b.updated_on || b.created_on).getTime() -
						new Date(a.updated_on || a.created_on).getTime()
				);

			if (copays.length > 0) {
				form.setFieldState("copay_card", (s) => {
					s.value = { ...copays[0] };
				});
			} else {
				form.setFieldState("copay_card", (s) => {
					s.value = "";
				});
				form.setFieldState("copay_unused", (s) => {
					s.value = "";
				});
			}
		} catch (err) {
			console.error("PrefillCopayCard: API error", err);
		}
	})();
	return "";
};

// ─── SetCopayUnusedAmt ──────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetCopayUnusedAmt
// Trigger field: copay_card
// Fetches the selected copay program and sets copay_unused to the remaining amount.
// Warns if amount is 0.
export const SetCopayUnusedAmt: FieldHandler = (field, { form }) => {
	void (async () => {
		const selectedCopay = form.getFieldState("copay_card")?.value?.id;
		if (!selectedCopay) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/patient_copay_program/list?limit=1&_status=eq:Approved&_id=eq:${selectedCopay}`
				)
			);
			const rows = Array.isArray(data) ? data : (data?.data ?? []);
			if (rows.length === 0) return;

			const [program] = rows;
			form.setFieldState("copay_unused", (s) => {
				s.value = program.amount_left;
			});

			if (parseFloat(program.amount_left) === 0) {
				field.setFeedback({
					type: "warning",
					messages: [
						"Copay Card Amount is 0. Cannot select copay as a fill type.",
					],
				});
				form.setFieldState("type", (s) => {
					s.value = "";
				});
				form.setFieldState("copay_card", (s) => {
					s.value = "";
				});
			}
		} catch (err) {
			console.error("SetCopayUnusedAmt: API error", err);
		}
	})();
	return "";
};

// ─── SetCopayCardAmount ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetCopayCardAmount
// Trigger field: claim_amount / patient_oop / plan_pay
// Calculates copay_card_paid_amt from claim_amount - patient_oop - plan_pay for Copay fills.
export const SetCopayCardAmount: FieldHandler = (field, { form, editID }) => {
	const fillType = form.getFieldState("type")?.value;
	if (fillType !== "Copay") return "";

	// Skip edit hydration; still runs when claim_amount changes after a user edit.
	if (editID && !isFieldUserModified(field)) return "";

	const claimAmount =
		parseFloat(form.getFieldState("claim_amount")?.value) || 0;
	if (claimAmount === 0) return "";

	const patientPay =
		parseFloat(form.getFieldState("patient_oop")?.value) || 0;
	const planPay = parseFloat(form.getFieldState("plan_pay")?.value) || 0;

	form.setFieldState("plan_amount", (s) => {
		s.value = planPay;
	});

	if (patientPay + planPay > claimAmount) {
		field.setFeedback({
			type: "warning",
			messages: [
				"Patient Pay Amount + Plan Pay Amount cannot be greater than Net Claim Amount",
			],
		});
		form.setFieldState("patient_oop", (s) => {
			s.value = 0;
		});
		form.setFieldState("plan_pay", (s) => {
			s.value = 0;
		});
		form.setFieldState("plan_amount", (s) => {
			s.value = 0;
		});
	} else {
		const copayAmount = claimAmount - patientPay - planPay;
		form.setFieldState("copay_card_paid_amt", (s) => {
			s.value = copayAmount;
		});
		form.setFieldState("funding_amount", (s) => {
			s.value = copayAmount;
		});
		field.setFeedback({ type: "warning", messages: [] });
	}
	return "";
};

// ─── Check340BAvailable ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.Check340BAvailable
// Trigger field: copay_card_paid_amt / copay_unused
// When fill type is Copay, checks if 340B is available and warns if copay will be exhausted.
export const Check340BAvailable: FieldHandler = (field, { form }) => {
	void (async () => {
		const fillType = form.getFieldState("type")?.value;
		if (fillType !== "Copay") return;

		const is340bAlready = form.getFieldState("is_340b_available")?.value;
		if (is340bAlready === "Yes") return;

		const medicationId = form.getFieldState("medication_id")?.value?.id;
		if (!medicationId) return;

		try {
			const invData = await fetchAPI(
				buildApiUrl(`/inventory/${medicationId}`)
			);
			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv?.brand_name_id?.id) return;

			const brandData = await fetchAPI(
				buildApiUrl(`/drug_brand_short/${inv.brand_name_id?.id}`)
			);
			const brand = Array.isArray(brandData) ? brandData[0] : brandData;
			if (brand?.discount_drug_program_available !== "Yes") return;

			const copayUnused =
				parseFloat(form.getFieldState("copay_unused")?.value) || 0;
			const copayPaidAmt =
				parseFloat(form.getFieldState("copay_card_paid_amt")?.value) ||
				0;

			if (copayPaidAmt >= copayUnused) {
				form.setFieldState("is_340b_available", (s) => {
					s.value = brand.discount_drug_program_available;
				});
				field.setFeedback({
					type: "warning",
					messages: [
						"Copay card will be exhausted after this fill. The drug has a 340B option available.",
					],
				});
			} else {
				form.setFieldState("is_340b_available", (s) => {
					s.value = "No";
				});
			}
		} catch (err) {
			console.error("Check340BAvailable: API error", err);
		}
	})();
	return "";
};
